import { dealOne } from './dealer';
import type { DealerState, DealStream } from './dealer';
import { parseFactId } from './facts';
import type { Fact, Operation, Tile } from './facts';
import { buildChoice4 } from './exercises/choice4';
import { assignRelations, findPartner, partnerSources } from './exercises/compare';
import type { Mode, Relation } from './exercises/compare';
import { presentedValue, truthSequence } from './exercises/truefalse';
import { shuffle } from './rng';
import type { Rng } from './rng';

export type ExerciseType = 'choice4' | 'truefalse' | 'compare';

const TYPE_CYCLE: readonly ExerciseType[] = ['choice4', 'truefalse', 'compare'];

/** Верхний предел числа заданий. Ограничение идёт не от интерфейса, а от
 *  `assignRelations`: она перебирает 3^n назначений, где n — число заданий
 *  на сравнение, то есть примерно треть от `taskCount`. При 30 заданиях это
 *  10 сравнений и 3^10 = 59 049 вариантов — мгновенно. При 60 было бы 20
 *  сравнений и 3^20 ≈ 3,5 млрд, то есть зависшее приложение.
 *  Реальные сессии используют 20 заданий, так что запас трёхкратный. */
const MAX_TASK_COUNT = 30;

export interface Task {
  id: string;
  index: number;
  type: ExerciseType;
  /** Операция слота. Для сравнения относится к левому выражению (решение (c)). */
  op: Operation;
  fact: Fact;
  partner?: Fact;
  relation?: Relation;
  options?: number[];
  presented?: number;
}

export interface BuildSessionParams {
  tiles: readonly Tile[];
  mode: Mode;
  taskCount: number;
  dealer: DealerState;
  rng: Rng;
  makeId: () => string;
}

/** Половина слотов — умножение, половина — деление, вперемешку. */
function balancedOps(count: number, rng: Rng): Operation[] {
  const half = Math.floor(count / 2);
  const ops: Operation[] = [
    ...Array<Operation>(half).fill('mul'),
    ...Array<Operation>(count - half).fill('div'),
  ];
  return shuffle(ops, rng);
}

/** Последовательность плиток, где каждая встречается не менее
 *  floor(count / tiles.length) раз (раздел 5.2). */
function stratifiedTiles(tiles: readonly Tile[], count: number, rng: Rng): Tile[] {
  const repeats = Math.ceil(count / tiles.length);
  const sequence: Tile[] = [];
  for (let i = 0; i < repeats; i++) sequence.push(...shuffle(tiles, rng));
  return shuffle(sequence.slice(0, count), rng);
}

export function buildSession(params: BuildSessionParams): { tasks: Task[]; dealer: DealerState } {
  const { tiles, mode, taskCount, rng, makeId } = params;
  if (!Number.isInteger(taskCount) || taskCount <= 0) {
    throw new Error(`buildSession: taskCount должен быть целым числом больше нуля, получено ${taskCount}`);
  }
  if (taskCount > MAX_TASK_COUNT) {
    throw new Error(
      `buildSession: taskCount ${taskCount} превышает предел ${MAX_TASK_COUNT} — ` +
        `перебор назначений исходов сравнения растёт как 3^n`,
    );
  }
  if (tiles.length === 0) throw new Error('buildSession: не выбрано ни одной плитки');

  const types = Array.from({ length: taskCount }, (_, i) => TYPE_CYCLE[i % TYPE_CYCLE.length]);
  const qualifying = types.map((t, i) => ({ t, i })).filter((x) => x.t !== 'compare').map((x) => x.i);
  const comparing = types.map((t, i) => ({ t, i })).filter((x) => x.t === 'compare').map((x) => x.i);

  // Операции: сначала зачётным слотам, потом сравнениям (раздел 5.2).
  const ops: Operation[] = Array(taskCount);
  if (mode === 'mixed') {
    balancedOps(qualifying.length, rng).forEach((op, n) => { ops[qualifying[n]] = op; });
    balancedOps(comparing.length, rng).forEach((op, n) => { ops[comparing[n]] = op; });
  } else {
    ops.fill(mode);
  }

  const tileSequence = stratifiedTiles(tiles, taskCount, rng);

  let dealer = params.dealer;
  const facts: Fact[] = [];
  for (let i = 0; i < taskCount; i++) {
    // Сравнения раздаются из отдельного потока: их выдачи не должны
    // случайно съедать курсор зачётных слотов (раздел 5.6 vs. 6.2).
    const stream: DealStream = types[i] === 'compare' ? 'compare' : 'main';
    const dealt = dealOne(dealer, ops[i], tileSequence[i], rng, stream);
    dealer = dealt.state;
    facts.push(parseFactId(dealt.factId));
  }

  // Исходы сравнения назначаются после выдачи левых выражений (раздел 5.6).
  const compareLefts = comparing.map((i) => facts[i]);
  const compareSources = comparing.map((i) => partnerSources(tiles, mode, ops[i]));
  const relations = compareLefts.length > 0
    ? assignRelations(compareLefts, compareSources, rng)
    : [];

  const truths = truthSequence(
    types.filter((t) => t === 'truefalse').length,
    rng,
  );
  let truthCursor = 0;

  const tasks: Task[] = facts.map((fact, i) => {
    const base: Task = { id: makeId(), index: i, type: types[i], op: ops[i], fact };
    if (types[i] === 'choice4') {
      const pool = partnerSources(tiles, mode, ops[i])[0];
      return { ...base, options: buildChoice4(fact, pool, rng) };
    }
    if (types[i] === 'truefalse') {
      return { ...base, presented: presentedValue(fact, truths[truthCursor++], rng) };
    }
    const slot = comparing.indexOf(i);
    const relation = relations[slot];
    return { ...base, relation, partner: findPartner(fact, relation, compareSources[slot], rng) };
  });

  /** Идентификаторы заданий обязаны быть различны.
   *
   *  Счётчики ошибок и подсказок ключуются по `task.id`. При дубле третья
   *  ошибка ОДНОГО задания закроет подсказкой ДРУГОЕ — ребёнок увидит
   *  ответ на пример, которого не решал, а факт получит строку раскрытия и
   *  потеряет освоенность на двенадцать часов. Отличить эту строку от
   *  настоящей потом нельзя: она выглядит как обычная.
   *
   *  `makeId` приходит доводом, то есть его качество — не наше дело, а вот
   *  последствия наши. Проверка стоит копейку: множество на двадцать строк.
   *  Из тех проверок, которые «никогда не сработают», — как та в
   *  `buildChoice4`, поймавшая ошибку в другом модуле. */
  const ids = new Set(tasks.map((t) => t.id));
  if (ids.size !== tasks.length) {
    throw new Error(`buildSession: makeId выдал повторяющиеся идентификаторы (${tasks.length} заданий, ${ids.size} различных)`);
  }

  return { tasks, dealer };
}
